import { useState } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('contact_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9etde0jnu01ovhhkr40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: formData.get('email') as string,
        }).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg(parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-3">
            Stay Connected
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-3">
            Join Our <span className="gradient-text">Newsletter</span>
          </h2>
          <p className="text-foreground-500 text-sm md:text-base mb-8">
            Get inspiring stories, campaign updates, and impact reports delivered straight to your inbox. No spam, just goodness.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto" data-readdy-form>
            <input
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <div className="relative flex-1 w-full">
              <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                className="w-full pl-11 pr-4 py-3.5 bg-background-50 border border-foreground-200 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-300"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto px-7 py-3.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 disabled:opacity-60 transition-all duration-300 whitespace-nowrap"
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Subscribing...
                </span>
              ) : (
                'Subscribe'
              )}
            </button>
          </form>

          {status === 'success' && (
            <p className="text-secondary-600 text-sm mt-3 font-medium">
              <i className="ri-check-line mr-1"></i>
              Welcome aboard! Check your inbox for a confirmation.
            </p>
          )}
          {status === 'error' && (
            <p className="text-primary-600 text-sm mt-3 font-medium">
              <i className="ri-error-warning-line mr-1"></i>
              {errorMsg}
            </p>
          )}

          <p className="text-foreground-400 text-[11px] mt-4">
            By subscribing, you agree to our Privacy Policy. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
}